
import React, { useState, useEffect } from "react";
import "flatpickr/dist/themes/material_blue.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

const SightSeeingProduct = () => {
  const { state } = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [pageLoading, setPageLoading] = useState(false);
  const [availLoading, setAvailLoading] = useState(false);
  const [productQuery, setProductQuery] = useState({});
  const [searchQuery, setSearchQuery] = useState({});
  const [product, setProduct] = useState({});

  const [showAdults, setShowAdults] = useState(true);
  const [showKids, setShowKids] = useState(false);
  const [showInfants, setShowInfants] = useState(false);

  const [travelerLimits, setTravelerLimits] = useState({
    adult: { min: 1, max: 15 },
    child: { min: 0, max: 0 },
    infant: { min: 0, max: 0 }
  });

  const [travelDate, setTravelDate] = useState(params.travelDate || "");
  const [adults, setAdults] = useState(parseInt(params.adults) || 1);
  const [kids, setKids] = useState(parseInt(params.kids) || 0);
  const [infants, setInfants] = useState(0);

  const [isCheckRequired, setIsCheckRequired] = useState(false);
  const [availabilityOptions, setAvailabilityOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);

  const [inclusions, setInclusions] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [informations, setInformations] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [productImages, setProductImages] = useState([]);

  const responsive = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  useEffect(() => {
    if (state === null || state.searchQuery === null) {
      navigate("/sightseeing/search");
      return;
    }
    setSearchQuery(state.searchQuery);
    setProductQuery(state.productQuery);

    // Initial Load: Chain starts here
    BindPageData(state.productQuery);
    setIsCheckRequired(false);
  }, []);

  const BindPageData = (query) => {
    setPageLoading(true);
    const formQuery = {
      productId: params.productId,
      supplierId: query?.supplierId,
    };
    GetProduct(formQuery, query);
  };

  const GetProduct = async (formQuery, currentProdQuery) => {
    try {
      const result = await apiService.post(config.SIGHTSEEINGPRODUCT, formQuery);

      if (result.succeeded && result.data) {
        setProduct(result.data);

        let apiAgeBands = [];
        if (result.data.pricingInfo?.ageBands) {
          apiAgeBands = result.data.pricingInfo.ageBands;
        } else if (result.data.ageBands) {
          apiAgeBands = result.data.ageBands;
        }

        // --- CALCULATE MIN/MAX AND AUTO-CORRECT COUNTS ---
        let finalAdults = adults;
        let finalKids = kids;
        let finalInfants = infants;

        if (apiAgeBands.length > 0) {
          const adBand = apiAgeBands.find(b => b.ageBand === "ADULT" || b.ageBand === "TRAVELER");
          const chBand = apiAgeBands.find(b => b.ageBand === "CHILD");
          const inBand = apiAgeBands.find(b => b.ageBand === "INFANT");

          const limits = {
            adult: { min: adBand?.minTravelersPerBooking || 1, max: adBand?.maxTravelersPerBooking || 15 },
            child: { min: chBand?.minTravelersPerBooking || 0, max: chBand?.maxTravelersPerBooking || 0 },
            infant: { min: inBand?.minTravelersPerBooking || 0, max: inBand?.maxTravelersPerBooking || 0 }
          };
          setTravelerLimits(limits);

          // Respect Minimums: If param is less than min, use min.
          finalAdults = Math.max(finalAdults, limits.adult.min);
          finalKids = Math.max(finalKids, limits.child.min);
          finalInfants = Math.max(finalInfants, limits.infant.min);

          setAdults(finalAdults);
          setKids(finalKids);
          setInfants(finalInfants);

          setShowAdults(limits.adult.max > 0);
          setShowKids(limits.child.max > 0);
          setShowInfants(limits.infant.max > 0);
        }

        // Set UI content
        setInclusions(result.data.productInclusions?.filter(i => i.description) || []);
        setExclusions(result.data.productExclusions?.filter(i => i.description) || []);
        setInformations(result.data.productInformation || []);
        setItineraries(result.data.productItineraries || []);
        setCancellations(result.data.productCancellationPolicy || []);
        setProductImages(result.data.productImages || []);

        // --- TRIGGER AVAILABILITY CHECK AUTOMATICALLY ---
        // Pass the corrected final counts directly to the check
        CheckAvailability(travelDate, finalAdults, finalKids, finalInfants, currentProdQuery, result.data);

      }
    } catch (error) {
      console.error("Error fetching product", error);
    }
    setPageLoading(false);
  };

  const CheckAvailability = async (date, ad, kd, inf, prodQueryOverride = null, productData = null) => {
    setAvailLoading(true);
    setIsAvailable(false);

    const currentProdQuery = prodQueryOverride || productQuery;
    const currentProduct = productData || product;

    let adultBand = "ADULT";
    // Check if the band name should be TRAVELER instead
    const bands = currentProduct.pricingInfo?.ageBands || currentProduct.ageBands || [];
    if (bands.some(b => b.ageBand === "TRAVELER")) {
      adultBand = "TRAVELER";
    }

    const payload = {
      supplierId: currentProdQuery.supplierId,
      productId: params.productId,
      travelDate: date,
      currencyCode: currentProdQuery.currencyCode || "INR",
      noOfAdults: ad,
      noOfKids: kd,
      noOfInfants: inf,
      adultBandName: adultBand,
    };

    try {
      const result = await apiService.post("SightSeeing/CheckAvailability", payload);
      if (result.succeeded && result.data && result.data.options.length > 0) {
        setAvailabilityOptions(result.data.options);
        setSelectedOption(result.data.options[0]);
        setIsAvailable(true);
        setIsCheckRequired(false);
      } else {
        setIsAvailable(false);
        setAvailabilityOptions([]);
        setSelectedOption(null);
      }
    } catch (error) {
      console.error(error);
      setIsAvailable(false);
    }
    setAvailLoading(false);
  };

  // --- HANDLERS (Same as before but respecting limits) ---
  const handleDateChange = (e) => {
    setTravelDate(e.target.value);
    setIsCheckRequired(true);
  };

  const handleIncrement = (type) => {
    if (type === "ADULT" && adults < travelerLimits.adult.max) setAdults(prev => prev + 1);
    if (type === "KIDS" && kids < travelerLimits.child.max) setKids(prev => prev + 1);
    if (type === "INFANTS" && infants < travelerLimits.infant.max) setInfants(prev => prev + 1);
    setIsCheckRequired(true);
  };

  const handleDecrement = (type) => {
    if (type === "ADULT" && adults > travelerLimits.adult.min) setAdults(prev => prev - 1);
    if (type === "KIDS" && kids > travelerLimits.child.min) setKids(prev => prev - 1);
    if (type === "INFANTS" && infants > travelerLimits.infant.min) setInfants(prev => prev - 1);
    setIsCheckRequired(true);
  };

  const handleBook = () => {
    if (!selectedOption) return;
    const bookingPayload = {
      sightSeeingBookingId: "00000000-0000-0000-0000-000000000000",
      userId: user?.id,
      branchId: user?.bId,
      supplierId: product.supplierId,
      bookingNo: "",
      discount: 0,
      couponCode: "",
      supplierBookingId: params.productId,
      productName: productQuery.productName,
      productURL: product.productURL,
      transactionNo: "",
      bookingDate: new Date(),
      noOfAdults: adults,
      noOfKids: kids,
      noOfInfants: infants,
      travelDate: travelDate,
      city: params.destination,
      travellerName: "",
      bookingStatus: "PENDING",
      paymentStatus: "PENDING",
      travellerInfos: [],
      invoiceNo: "",
      transactionFee: 0,
      totalPrice: selectedOption.salePrice,
      gst: 0,
      gstAmount: 0,
      paymentGatewayFee: 0,
      markup: 0,
      customerPrice: selectedOption.salePrice,
      bookingType: "SIGHTSEEING",
      gatewayAmount: 0,
      walletAmount: 0,
      creditAmount: 0,
      paymentGatewayNo: "",
      paymentGateway: "RAZORPAY",
      email: "",
      mobile: "",
      requestedBy: "CUSTOMER",
      price: selectedOption.salePrice,
      currencyCode: productQuery.currencyCode,
      productOptionCode: selectedOption.productOptionCode,
      startTime: selectedOption.startTime
    };
    navigate(`/sightseeing/booking/${params.productId}/${params.destinationCode}/${params.destination}/${adults}/${kids}/${infants}/${travelDate}`,
      { state: { searchQuery: bookingPayload, product } }
    );
  };


  if (loading) return <p>Loading...</p>;

  return (
    <div id="layout-wrapper">
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card pt-0">
              <div className="card-body">
                {pageLoading ? (
                  <div className="text-center">
                    <img src="/assets/images/loader.webp" width={"64px"} height={"64px"} alt="loading" />
                    Loading...
                  </div>
                ) : (
                  <div className="row">
                    {/* Left Side (Details) */}
                    <div className="col-lg-8">
                      <div className="bg-white shadow-md rounded p-3 p-sm-4 confirm-details">
                        <h4 className="mb-1">{product.productName}</h4>
                        <div className="owl-carousel owl-theme single-slider mb-3">
                          <Carousel responsive={responsive}>
                            {productImages.length > 0 &&
                              productImages.map((pi, index) => (
                                <div className="item" key={index}>
                                  <Link to={pi.imageUrl} target="_blank">
                                    <img className="img-fluid" src={pi.imageUrl} alt="Tour" style={{ minWidth: "800px" }} />
                                  </Link>
                                </div>
                              ))}
                          </Carousel>
                        </div>

                        <nav id="navbar-sticky" className="bg-white pb-2 mb-2 sticky-top smooth-scroll">
                          <ul className="nav nav-tabs">
                            <li className="nav-item"> <a className="nav-link active" href="#known-for"> Information </a> </li>
                            <li className="nav-item"> <a className="nav-link" href="#inclusions"> Inclusions </a> </li>
                            <li className="nav-item"> <a className="nav-link" href="#exclusions"> Exclusion </a> </li>
                            <li className="nav-item"> <a className="nav-link" href="#cancellations"> Cancellation </a> </li>
                            <li className="nav-item"> <a className="nav-link" href="#itineraries"> Itinerary </a> </li>
                          </ul>
                        </nav>

                        <p id="known-for">{product.description}</p>
                        <div className="row">
                          <div className="col-12 col-sm-12">
                            <ol className="simple-ul">
                              {informations.map((x, i) => (<li key={i} className="mb-2"> {x.description} </li>))}
                            </ol>
                          </div>
                        </div>

                        <hr className="mb-4" />
                        <h4 id="inclusions" className="text-6 mb-4 mt-2"> Inclusions </h4>
                        <div className="row"> <div className="col-12 col-sm-12"> <ol className="simple-ul"> {inclusions.map((x, i) => (<li key={i}>{x.description}</li>))} </ol> </div> </div>

                        <hr className="mb-4" />
                        <h4 id="exclusions" className="text-6 mb-4 mt-2"> Exclusions </h4>
                        <div className="row"> <div className="col-12 col-sm-12"> <ol className="simple-ul"> {exclusions.map((x, i) => (<li key={i}>{x.description}</li>))} </ol> </div> </div>

                        <hr className="mb-4" />
                        <h4 id="cancellations" className="text-6 mb-4 mt-2"> Cancellation </h4>
                        <div className="row"> <div className="col-12 col-sm-12"> <p>{cancellations.description}</p> </div> </div>

                        <hr className="mb-4" />
                        <h4 id="itineraries" className="text-6 mb-4 mt-2"> Itinerary </h4>
                        <div className="row"> <div className="col-12 col-sm-12"> <ol className="simple-ul"> {itineraries.map((x, i) => (<li key={i}>{x.description}</li>))} </ol> </div> </div>
                      </div>
                    </div>

                    {/* --- RIGHT SIDE: DYNAMIC INPUTS --- */}
                    <aside className="col-lg-4 mt-4 mt-lg-0">
                      <div className="side-block card rounded-2 p-3">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <h5 className="fw-semibold fs-6 mb-0">Check Availability</h5>
                        </div>
                        <div className="card mb-4 mt-lg-0 mt-4">
                          <div className="card-body py-2">
                            <div className="price-summary">
                              {/* 1. Date Picker */}
                              <div className="mb-3">
                                <label className="form-label fw-500 text-dark">Travel Date</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={travelDate}
                                  min={new Date().toISOString().split("T")[0]}
                                  onChange={handleDateChange}
                                />
                              </div>

                              {/* NEW: Display Minimum/Maximum Requirement */}
                              <div className="alert alert-light border p-2 mb-3" style={{ fontSize: '12px', color: '#555' }}>
                                <strong>Requirements:</strong>
                                <ul className="mb-0 ps-3">
                                  {showAdults && <li>Adults: Min {travelerLimits.adult.min}, Max {travelerLimits.adult.max}</li>}
                                  {showKids && <li>Kids: Min {travelerLimits.child.min}, Max {travelerLimits.child.max}</li>}
                                  {showInfants && <li>Infants: Min {travelerLimits.infant.min}, Max {travelerLimits.infant.max}</li>}
                                </ul>
                              </div>

                              {/* 2. Dynamic Travelers Inputs with +/- Buttons */}
                              <div className="mb-3">
                                {/* ADULTS */}
                                {showAdults && (
                                  <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                                    <span className="fw-500 text-dark">Adults</span>
                                    <div className="d-flex align-items-center">
                                      <button
                                        className="btn btn-light btn-sm border"
                                        onClick={() => handleDecrement("ADULT")}
                                        disabled={adults <= travelerLimits.adult.min} // Disable if at min
                                        style={{ width: "30px", height: "30px", padding: 0 }}
                                      >
                                        −
                                      </button>
                                      <span className="mx-3 fw-bold">{adults}</span>
                                      <button
                                        className="btn btn-light btn-sm border"
                                        onClick={() => handleIncrement("ADULT")}
                                        disabled={adults >= travelerLimits.adult.max} // Disable if at max
                                        style={{ width: "30px", height: "30px", padding: 0 }}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* KIDS */}
                                {showKids && (
                                  <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                                    <span className="fw-500 text-dark">Kids</span>
                                    <div className="d-flex align-items-center">
                                      <button
                                        className="btn btn-light btn-sm border"
                                        onClick={() => handleDecrement("KIDS")}
                                        disabled={kids <= travelerLimits.child.min}
                                        style={{ width: "30px", height: "30px", padding: 0 }}
                                      >
                                        −
                                      </button>
                                      <span className="mx-3 fw-bold">{kids}</span>
                                      <button
                                        className="btn btn-light btn-sm border"
                                        onClick={() => handleIncrement("KIDS")}
                                        disabled={kids >= travelerLimits.child.max}
                                        style={{ width: "30px", height: "30px", padding: 0 }}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* INFANTS */}
                                {showInfants && (
                                  <div className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                                    <span className="fw-500 text-dark">Infants</span>
                                    <div className="d-flex align-items-center">
                                      <button
                                        className="btn btn-light btn-sm border"
                                        onClick={() => handleDecrement("INFANTS")}
                                        disabled={infants <= travelerLimits.infant.min}
                                        style={{ width: "30px", height: "30px", padding: 0 }}
                                      >
                                        −
                                      </button>
                                      <span className="mx-3 fw-bold">{infants}</span>
                                      <button
                                        className="btn btn-light btn-sm border"
                                        onClick={() => handleIncrement("INFANTS")}
                                        disabled={infants >= travelerLimits.infant.max}
                                        style={{ width: "30px", height: "30px", padding: 0 }}
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* 3. Check Availability Button */}
                              <button
                                className="btn btn-primary w-100 btn-sm mb-3"
                                onClick={() => CheckAvailability(travelDate, adults, kids, infants)}
                                disabled={!isCheckRequired || availLoading}
                              >
                                {availLoading ? "Checking..." : "Check Availability"}
                              </button>

                              <hr />

                              {/* 4. Time Slot Dropdown */}
                              {availabilityOptions.length > 1 && (
                                <div className="mb-3 mt-3">
                                  <label className="form-label fw-500">Select Time Slot:</label>
                                  <select
                                    className="form-select"
                                    onChange={(e) => {
                                      const opt = availabilityOptions.find(
                                        (o) => o.productOptionCode === e.target.value
                                      );
                                      setSelectedOption(opt);
                                    }}
                                  >
                                    {availabilityOptions.map((opt) => (
                                      <option key={opt.productOptionCode} value={opt.productOptionCode}>
                                        {opt.startTime}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* 5. Pricing Footer */}
                          <div className="card-footer bg-white border-top py-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <p className="fw-semibold text-muted-2 mb-0">Total Price</p>
                              <p className="fw-semibold text-primary mb-0" style={{ fontSize: "1.2rem" }}>
                                {availLoading ? (
                                  "..."
                                ) : !isAvailable ? (
                                  <span className="text-danger">Sold Out</span>
                                ) : (
                                  <>
                                    {searchQuery.currencyCode}{" "}
                                    {selectedOption ? Math.round(selectedOption.salePrice) : "..."}
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="d-grid mt-2">
                              <button
                                className="btn btn-primary"
                                onClick={() => handleBook()}
                                disabled={isCheckRequired || availLoading || !isAvailable || !selectedOption}
                              >
                                {isCheckRequired
                                  ? "Update Details First"
                                  : availLoading
                                    ? "Checking..."
                                    : isAvailable
                                      ? "Book Now"
                                      : "Not Available"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </aside>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SightSeeingProduct;